import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, shareReplay } from 'rxjs';

import { Notifications } from '../../../enum/notifications';
import { GuestDTO } from '../../../model/guestDTO';
import { Offer } from '../../../model/offer';
import { AuthService } from '../../../_services/auth.service';
import { OfferService } from '../../../_services/offer/offer.service';
import { SharedService } from '../../../_services/shared.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { ShoppingCartTableComponent } from '../../shopping-cart-table/shopping-cart-table.component';

@Component({
  selector: 'ui-new-offer-photo-terms',
  standalone: true,
  imports: [CommonModule, ShoppingCartTableComponent],
  templateUrl: './ui-new-offer-photo-terms.component.html',
  styleUrl: './ui-new-offer-photo-terms.component.scss',
})
export class UiNewOfferPhotoTermsComponent implements OnInit {
  @Input() offer!: Offer | null;
  @Input() offers: Offer[] = [];
  @Input() propertyId: string = '';
  @Input() offerAliasId: string = '';

  acknowledgment = false;

  constructor(
    private authService: AuthService,
    private offerService: OfferService,
    private router: Router,
    private route: ActivatedRoute,
    public sharedService: SharedService,
    public shoppingCartService: ShoppingCartService,
    public modalRef: BsModalRef,
  ) {}

  ngOnInit(): void {
    // responsive handled via lxViewport in template (if used)
  }

  toggleAcknowledgment(): void {
    this.acknowledgment = !this.acknowledgment;
  }

  getGuestInfo(offer_alias_id: string, prop_id: string): Observable<GuestDTO> {
    return this.offerService.getGuestInfo(offer_alias_id, prop_id).pipe(shareReplay(1));
  }

  acceptOffer(): void {
    if (!this.offer || !this.acknowledgment) return;

    setTimeout(() => {
      this.closeModal();
    }, 0);

    localStorage.setItem('offer', JSON.stringify(this.offer));

     const requestedOfferSetIds: string[] = [];
      const requestedOfferIds: string[] = [];

      for (const offer of this.offers) {
        requestedOfferSetIds.push(offer.offer_set_id);
        requestedOfferIds.push(offer.offer_id);
      }

      try {
        this.offerService.requestUpgrades(this.offerAliasId, this.propertyId, requestedOfferSetIds, requestedOfferIds).subscribe();
      } catch (error) {
        this.closeModal();
        this.router.navigate(['/offerNotAvailable']);
      }

    const guestInfo$ = this.getGuestInfo(this.offerAliasId, this.propertyId).pipe(shareReplay(1));
    guestInfo$.subscribe(res => {
      const guest_info: GuestDTO = res;

      if (!this.offer) return;

      if (this.offer.confirmation_type === Notifications.email && guest_info.has_guest_email === false) {
        this.closeModal();
        this.navigateToOfferFormEmail();
        return;
      }

      if (
        this.offer.confirmation_type === Notifications.email ||
        this.offer.confirmation_type === Notifications.emailOrSms
      ) {
        this.authService
          .requestUpgradeConfirmationValidation(
            this.offerAliasId,
            this.propertyId,
            this.offer.offer_set_id,
            this.offer.offer_id,
            this.offer.base_room_category_name,
            this.offer.bar_price,
          )
          .subscribe({
            next: (data) => {
              this.closeModal();
              if (data.data === true || data.data === 'true') {
                this.navigateConfirmationValidation();
              } else {
                this.offerService.confirmOfferAccepted(this.propertyId, 
                                                       this.offerAliasId, 
                                                       requestedOfferSetIds, 
                                                       requestedOfferIds,
                                                       ''
                                                      ).subscribe({
                                                        next: () => {
                                                          this.navigateRequestThankYou();
                                                        },
                                                        error: (error) => {
                                                          console.error('Failed to confirm offer:', error);
                                                          this.router.navigate(['/offerNotAvailable']);
                                                        }
                                                      });              
              }
            },
            error: (error) => {
              this.closeModal();
              console.error('Failed to validate confirmation:', error);
              this.router.navigate(['/offerNotAvailable']);
            }
          });
      }
    });
  }

  closeModal(): void {
    this.modalRef.hide();
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.classList.remove('modal-open');
    document.body.classList.remove('modal-open');
  }

  emptyCart(requestOnlyMode: boolean = false): void {
    if (requestOnlyMode) {
      this.shoppingCartService.emptyCart(this.offerAliasId);
    }
  }

  closeModalAndEmptyCart(): void {
    this.emptyCart(this.sharedService.requestOnlyMode);
    this.closeModal();
  }

  navigateToOfferConfirmation(): void {
    this.router.navigate(['/offerConfirmation/' + this.offerAliasId + '/' + this.propertyId], { relativeTo: this.route });
  }

  navigateConfirmationValidation(): void {
    this.router.navigate(['/offerConfirmationValidation/' + this.offerAliasId + '/' + this.propertyId], {
      relativeTo: this.route,
    });
  }

  navigateRequestThankYou(): void {
    this.router.navigate(['/offerConfirmation/' + this.offerAliasId + '/' + this.propertyId + '/0' ], {
      relativeTo: this.route,
    });
  }

  navigateToOfferFormEmail(): void {
    this.router.navigate(['/offerGuestEmail/' + this.offerAliasId + '/' + this.propertyId], { relativeTo: this.route });
  }

  navigatePrivacy(): void {
    window.open(`${window.location.origin}/privacyPolicy`, '_blank');
  }

  navigateTerms(): void {
    window.open(`${window.location.origin}/termsOfService`, '_blank');
  }

  notifications = {
    E: "You'll receive an email with the outcome. If we're unable to fulfill your request, it's likely due to availability.",
    S: "You'll receive an SMS update on whether your upgrade request has been accepted",
    ES: "You'll receive an email or SMS update on whether your upgrade request has been accepted",
    L: "You'll be given a request status link indicating whether your upgrade request has been accepted.  Additionally, upon check-in, a hotel team member will advise if your upgrade has been accepted.",
    N: 'Upon check-in, a hotel team member will advise if your upgrade request has been accepted.',
  };

  getNotification(conf_type: string): string {
    const key = conf_type as keyof typeof this.notifications;
    return this.notifications[key] ?? this.notifications.E;
  }

  calculateTotalQuantity(offer: any[]): number {
    return this.shoppingCartService.calculateTotalQuantity(offer);
  }

  calculateTotalPrice(offer: any[]): number {
    return this.shoppingCartService.calculateTotalPrice(offer);
  }

  calculateQuantity(offer: any): number {
    return this.shoppingCartService.calculateQuantity(offer);
  }

  calculatePrice(offer: any): number {
    return this.shoppingCartService.calculatePrice(offer);
  }

  removeOfferFromCart(event: { offer: any; aliasId?: string }): void {
    this.shoppingCartService.removeOfferFromCart(event.offer, event.aliasId!);
    this.offers = this.offers.filter(o => o !== event.offer);

    if (this.offers.length === 0) {
      this.closeModal();
    }
  }
}
